import { DiscountModeEnum } from '../../common/enums/invoicing.enum';
import { formatMoney, parseMoneyInput } from './money.helper';

/**
 * Discounts the way the backend prices them, so the editor's totals do not jump on save.
 *
 * In minor units, as the backend: PERCENT takes round(net x value / 100) of its line,
 * AMOUNT takes its value, and the discounts on one line together never take more than
 * the line - the last one is cut to what is left.
 */

export interface DiscountInput {
  mode: DiscountModeEnum;
  /** null while the field is empty or not a number. */
  value: number | null;
}

const toMinor = (amount: number) => Math.round(amount * 100);

/** What each discount takes off a line of `targetNet`, in order, never more than is left. */
export const discountAmounts = (targetNet: number, discounts: DiscountInput[]): number[] => {
  let leftMinor = Math.max(toMinor(targetNet), 0);
  return discounts.map(discount => {
    if (discount.value === null || !(discount.value > 0)) return 0;
    const wanted =
      discount.mode === DiscountModeEnum.PERCENT ? Math.round((toMinor(targetNet) * discount.value) / 100) : toMinor(discount.value);
    const taken = Math.max(Math.min(wanted, leftMinor), 0);
    leftMinor -= taken;
    return taken / 100;
  });
};

/** The value field's text as a number: "10" or "10,5" for a percentage, "1.200,00" for an amount. */
export const parseDiscountValue = (text: string): number | null => {
  const parsed = parseMoneyInput(text ?? '');
  return typeof parsed === 'number' ? parsed : null;
};

export type DiscountErrorKey = 'labelEmpty' | 'notPositive' | 'percentMax' | 'invalid' | 'moreThanLine' | 'sumMoreThanLine';

export interface DiscountProblem {
  label?: string;
  value?: string;
}

/**
 * What is wrong with each discount on one line, keyed like `discounts`.
 *
 * `targetNet` is null for a line whose amount is not known yet - an attendance line before
 * the days are counted - which leaves the amount checks to the server's cap.
 */
export const discountProblems = (
  t: (key: string, options?: Record<string, unknown>) => string,
  targetNet: number | null,
  discounts: { label: string; mode: DiscountModeEnum; valueText: string }[]
): DiscountProblem[] => {
  let used = 0;
  return discounts.map((discount, index) => {
    const problem: DiscountProblem = {};
    if (!discount.label.trim()) problem.label = t('discounts.validation.labelEmpty');
    const value = parseDiscountValue(discount.valueText);
    if (value === null) {
      problem.value = discount.valueText.trim() ? t('discounts.validation.invalid') : t('discounts.validation.notPositive');
      return problem;
    }
    if (!(value > 0)) {
      problem.value = t('discounts.validation.notPositive');
      return problem;
    }
    if (discount.mode === DiscountModeEnum.PERCENT && value > 100) {
      problem.value = t('discounts.validation.percentMax');
      return problem;
    }
    if (targetNet === null) return problem;
    const amount = discount.mode === DiscountModeEnum.PERCENT ? Math.round(toMinor(targetNet) * value) / 10000 : value;
    if (discount.mode === DiscountModeEnum.AMOUNT && value > targetNet) {
      problem.value = t('discounts.validation.moreThanLine', { amount: formatMoney(targetNet) });
      return problem;
    }
    used += amount;
    // the one that tips the sum over is the one marked: it is the last added
    if (toMinor(used) > toMinor(targetNet) && index > 0) problem.value = t('discounts.validation.sumMoreThanLine', { amount: formatMoney(targetNet) });
    return problem;
  });
};
